/**
 * Reactive auth state — subscribes to Supabase's auth change events and
 * mirrors the current session into React state. All SPA components that
 * need to know "who is this" read from here.
 *
 * V6: Adds OTP, Google/GitHub SSO, password reset, SSO detection.
 */
import { useCallback, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { getSupabase, isSupabaseEnabled } from "@/lib/supabase";

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
  isSSO: boolean;
  provider: string;
  supabaseEnabled: boolean;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string, captchaToken?: string) => Promise<{ error?: string; message?: string; confirmationRequired?: boolean }>;
  sendOtp: (email: string, isSignUp?: boolean, captchaToken?: string) => Promise<{ error?: string; message?: string }>;
  verifyOtp: (email: string, token: string) => Promise<{ error?: string; message?: string }>;
  signInWithGoogle: () => Promise<{ error?: string }>;
  signInWithGitHub: () => Promise<{ error?: string }>;
  resetPassword: (email: string, captchaToken?: string) => Promise<{ error?: string; message?: string }>;
  verifyResetPassword: (email: string, token: string, newPassword: string) => Promise<{ error?: string; message?: string }>;
  updatePassword: (currentPassword: string, newPassword: string, captchaToken?: string) => Promise<{ error?: string; message?: string }>;
  checkEmailAuthMethod: (email: string, captchaToken?: string) => Promise<{ exists: boolean; provider: string; blocked: boolean; message?: string }>;
  signOut: () => Promise<void>;
}

export function useSession(): AuthState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(isSupabaseEnabled());

  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) {
      setLoading(false);
      return;
    }
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) {
        setSession(data.session);
        setLoading(false);
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (mounted) {
        setSession(next);
        if (event === "SIGNED_OUT") {
          setSession(null);
        }
      }
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Detect SSO provider from user metadata
  const user = session?.user ?? null;
  const provider = user?.app_metadata?.provider ?? "email";
  const isSSO = provider === "google" || provider === "github";

  const signIn = useCallback(async (email: string, password: string, captchaToken?: string) => {
    const supabase = getSupabase();
    if (!supabase) return { error: "Auth is not configured on this deployment." };
    try {
      const { signinViaApi } = await import("@/lib/api");
      const result = await signinViaApi(email, password, captchaToken);
      if (result.ok && result.session?.access_token) {
        await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token ?? "",
        });
        return {};
      }
      return { error: "Sign-in failed — please try again." };
    } catch (e) {
      if (e instanceof Error) return { error: e.message };
      return { error: "Sign-in failed — please try again." };
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, captchaToken?: string) => {
    try {
      const { signupViaApi } = await import("@/lib/api");
      const result = await signupViaApi(email, password, captchaToken);
      if (result.ok) {
        return {
          message: result.message ?? "Account created. Check your inbox for a verification code.",
          confirmationRequired: result.confirmation_required ?? true,
        };
      }
      return { error: "Signup failed — please try again." };
    } catch (e) {
      if (e instanceof Error) return { error: e.message };
      return { error: "Signup failed — please try again." };
    }
  }, []);

  const sendOtp = useCallback(async (email: string, isSignUp = false, captchaToken?: string) => {
    try {
      const { sendOtpApi } = await import("@/lib/api");
      const result = await sendOtpApi(email, isSignUp, captchaToken);
      return { message: result.message ?? "OTP sent — check your inbox." };
    } catch (e) {
      if (e instanceof Error) return { error: e.message };
      return { error: "Failed to send OTP." };
    }
  }, []);

  const verifyOtp = useCallback(async (email: string, token: string) => {
    try {
      const { verifyOtpApi } = await import("@/lib/api");
      const result = await verifyOtpApi(email, token, "email");
      if (result.session?.access_token) {
        const supabase = getSupabase();
        if (supabase) {
          await supabase.auth.setSession({
            access_token: result.session.access_token,
            refresh_token: result.session.refresh_token ?? "",
          });
        }
        return { message: "Verified — you're in." };
      }
      return { error: "OTP verification did not return a session." };
    } catch (e) {
      if (e instanceof Error) return { error: e.message };
      return { error: "OTP verification failed." };
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const supabase = getSupabase();
    if (!supabase) return { error: "Auth is not configured on this deployment." };
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    return error ? { error: error.message } : {};
  }, []);

  const signInWithGitHub = useCallback(async () => {
    const supabase = getSupabase();
    if (!supabase) return { error: "Auth is not configured on this deployment." };
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "github",
      options: { redirectTo: window.location.origin },
    });
    return error ? { error: error.message } : {};
  }, []);

  const resetPassword = useCallback(async (email: string, captchaToken?: string) => {
    try {
      const { resetPasswordApi } = await import("@/lib/api");
      const result = await resetPasswordApi(email, captchaToken);
      return { message: result.message ?? "Recovery code sent — check your inbox." };
    } catch (e) {
      if (e instanceof Error) return { error: e.message };
      return { error: "Failed to send recovery code." };
    }
  }, []);

  const verifyResetPassword = useCallback(async (email: string, token: string, newPassword: string) => {
    try {
      const { verifyResetPasswordApi } = await import("@/lib/api");
      const result = await verifyResetPasswordApi(email, token, newPassword);
      if (result.ok) {
        return { message: result.message ?? "Password reset successfully." };
      }
      return { error: "Password reset failed." };
    } catch (e) {
      if (e instanceof Error) return { error: e.message };
      return { error: "Password reset failed." };
    }
  }, []);

  const updatePassword = useCallback(async (currentPassword: string, newPassword: string, captchaToken?: string) => {
    try {
      const { changePassword } = await import("@/lib/api");
      const result = await changePassword(currentPassword, newPassword, captchaToken);
      if (result.ok) {
        return { message: result.message ?? "Password updated successfully." };
      }
      return { error: "Password update failed." };
    } catch (e) {
      if (e instanceof Error) return { error: e.message };
      return { error: "Password update failed." };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await fetch("/api/v1/auth/logout", { method: "POST", credentials: "include" });
    } catch {
      /* logout is best-effort — if the backend is down we still clear the session */
    }
    const supabase = getSupabase();
    if (!supabase) return;
    await supabase.auth.signOut();
  }, []);

  const checkEmailAuthMethod = useCallback(async (email: string, captchaToken?: string) => {
    try {
      const { checkEmail } = await import("@/lib/api");
      const result = await checkEmail(email, captchaToken);
      return {
        exists: result.exists,
        provider: result.provider ?? "email",
        blocked: result.blocked,
        message: result.message,
      };
    } catch {
      return { exists: false, provider: "email", blocked: false };
    }
  }, []);

  return {
    user,
    session,
    loading,
    isAuthenticated: !!session?.user,
    isSSO,
    provider,
    supabaseEnabled: isSupabaseEnabled(),
    signIn,
    signUp,
    sendOtp,
    verifyOtp,
    signInWithGoogle,
    signInWithGitHub,
    resetPassword,
    verifyResetPassword,
    updatePassword,
    checkEmailAuthMethod,
    signOut,
  };
}
